import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPlanningsApi } from "../../http/carPlannings/api";
import {
  CarPurchase,
  getCarPurchaseStates,
  getPurchaseTypes,
  PurchaseType,
  State,
} from "../../http/carPurchases/types";
import { useLeasingCompaniesApi } from "../../http/leasingCompanies/api";
import { LeasingCompany } from "../../http/leasingCompanies/types";
import { useManufacturersApi } from "../../http/manufacturers/api";
import { Manufacturer } from "../../http/manufacturers/types";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import { useSuppliersApi } from "../../http/suppliers/api";
import { Supplier } from "../../http/suppliers/types";
import { useUsersApi } from "../../http/users/api";
import { User } from "../../http/users/types";
import { Currency, getCurrencies } from "../../types";
import Button from "../ui/Button";
import CostInput from "../ui/CostInput";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormGrid from "../ui/FormGrid";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Textarea from "../ui/Textarea";

const SUPPLIER_FIELDS = ["id", "name"] as const;
type SupplierField = (typeof SUPPLIER_FIELDS)[number];
type SupplierSearch = Pick<Supplier, SupplierField>;

const MANUFACTURER_FIELDS = ["id", "name"] as const;
type ManufacturerField = (typeof MANUFACTURER_FIELDS)[number];
type ManufacturerSearch = Pick<Manufacturer, ManufacturerField>;

const LEASING_COMPANY_FIELDS = ["id", "name"] as const;
type LeasingCompanyFiels = (typeof LEASING_COMPANY_FIELDS)[number];
type LeasingCompanySearch = Pick<LeasingCompany, LeasingCompanyFiels>;

const USER_FIELDS = ["id", "fullName"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

const SUBSIDIARIES_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARIES_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

export interface CarPurchaseFormBody {
  subsidiary: SubsidiarySearch;
  supplier?: SupplierSearch;
  leasingCompany?: LeasingCompanySearch;
  driver: Omit<UserSearch, "manager">;
  manufacturer: ManufacturerSearch;
  model: string;
  plannedArrivalDate: string;
  purchaseDate: string;
  costNet: number | null;
  costGross: number | null;
  currency: Currency | null;
  state: State;
  optionals: string;
  purchaseType: PurchaseType;
  assign: boolean;
}

type CarPurchaseFormProps = {
  carPurchase?: CarPurchase | null;
  onSubmit: (form: CarPurchaseFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const CarPurchaseForm: FC<CarPurchaseFormProps> = ({
  carPurchase,
  onSubmit,
  isDisabled,
  isLoading,
}: CarPurchaseFormProps) => {
  const i18n = useI18n();
  const subsidiaryApi = useSubsidiariesApi();
  const supplierApi = useSuppliersApi();
  const leasingCompanyApi = useLeasingCompaniesApi();
  const usersApi = useUsersApi();
  const manufacturerApi = useManufacturersApi();
  const carPlanningApi = useCarPlanningsApi();
  const aborter = useRef(new AbortController());
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);

  const [defaultUserOptions, setDefaultUserOptions] = useState<UserSearch[]>([]);

  const purchaseTypes = getPurchaseTypes(i18n.t);
  const states = getCarPurchaseStates(i18n.t);
  const currencies = getCurrencies();
  const euroCurrency = currencies.find((x) => x.symbol == "€");
  const subsidiaries = subsidiaryApi.useAdminSubsidiaries(
    { limit: 100, fields: SUBSIDIARIES_FIELDS },
    aborter.current.signal,
    !isDisabled,
  );
  const suppliers = supplierApi.useAdminSuppliers(
    { limit: 100, fields: SUPPLIER_FIELDS },
    aborter.current.signal,
    !isDisabled,
  );
  const leasingCompanies = leasingCompanyApi.useAdminLeasingCompanies(
    { limit: 100, fields: LEASING_COMPANY_FIELDS },
    aborter.current.signal,
    !isDisabled,
  );
  const manufacturers = manufacturerApi.useAdminManufacturers(
    { limit: 100, fields: MANUFACTURER_FIELDS },
    aborter.current.signal,
  );

  const c = carPurchase?.costNet
    ? carPurchase.costNet.ForeignValue
      ? carPurchase.costNet.ForeignValue.Currency
      : euroCurrency?.value
    : carPurchase?.costGross
      ? carPurchase.costGross.ForeignValue
        ? carPurchase.costGross.ForeignValue.Currency
        : euroCurrency?.value
      : undefined;

  const param = new URLSearchParams(window.location.search);
  const carPlanningId = param.get("car_planning_id");

  const { data, trigger } = carPlanningApi.getAdminCarPlanning(carPlanningId!, aborter.current.signal);

  useEffect(() => {
    carPlanningId && trigger();
  }, [carPlanningId]);

  const defaultValues = {
    subsidiary: data?.subsidiary ?? carPurchase?.subsidiary,
    supplier: carPurchase?.supplier,
    leasingCompany: carPurchase?.leasingCompany,
    driver: carPurchase?.driver,
    manufacturer: carPurchase?.manufacturer,
    model: carPurchase?.model,
    plannedArrivalDate: carPurchase?.plannedArrivalDate || undefined,
    purchaseDate: carPurchase?.purchaseDate || undefined,
    costNet: !!carPurchase?.costNet
      ? carPurchase.costNet.ForeignValue
        ? carPurchase.costNet.ForeignValue.Value
        : carPurchase.costNet.EuroValue
      : undefined,
    costGross: !!carPurchase?.costGross
      ? carPurchase.costGross.ForeignValue
        ? carPurchase.costGross.ForeignValue.Value
        : carPurchase.costGross.EuroValue
      : undefined,
    currency: currencies.find((x) => x.value == c),
    state: states.find((x) => x.value === carPurchase?.state),
    optionals: carPurchase?.optionals,
    purchaseType:
      purchaseTypes.find((x) => x.value === carPurchase?.purchaseType) ??
      purchaseTypes.find((x) => {
        if (data?.type == "new-purchase") {
          return x.value === "purchase";
        } else {
          return x.value === "leasing";
        }
      }),
    assign: false,
  };

  const handleAssign = useRef<(() => Promise<void> | undefined) | undefined>(undefined);

  return (
    <Form<CarPurchaseFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      secondaryButton={
        carPurchase?.state !== "assigned" && carPurchase?.id ? (
          <Button
            buttonProps={{
              type: "button",
              onClick: () => handleAssign.current?.(),
              disabled: isDisabled || isLoading,
            }}
            type="secondary"
          >
            {i18n.t.common.assignCar}
          </Button>
        ) : undefined
      }
      itemCreationInfo={carPurchase}
    >
      {({ register, formState: { errors }, control, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);

          handleAssign.current = async () => {
            const data = watch();
            data.assign = true;
            try {
              await onSubmit(data);
              navigate(`/admin/cars/new?car_purchase_id=${carPurchase?.id}`);
            } catch (error) {
              dispatch(showErrorNotification(i18n, error));
            }
          };
        }, [carPurchase, data]);

        const type = watch("purchaseType");

        return (
          <FormGrid columns={2}>
            <Controller
              name="subsidiary"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.subsidiaries.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      options={subsidiaries.data?.items}
                      isLoading={isLoading}
                      isDisabled={isDisabled || isLoading}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<SubsidiarySearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="driver"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.recipient} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions<UserSearch, UserField>(
                      aborter,
                      searchTerm,
                      usersApi.getAdminUsers,
                      { fields: USER_FIELDS },
                    );

                    // Fetch initial data on mount
                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultUserOptions);
                    }, []);

                    return (
                      <AsyncSelect
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultUserOptions}
                        loadOptions={loadOptions}
                        isLoading={isLoading}
                        isDisabled={isDisabled || isLoading}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.fullName}
                        getOptionValue={(x) => x.id}
                        styles={reactSelectStyles<UserSearch>(isInvalid)}
                        isClearable
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <Controller
              name="purchaseType"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.purchaseType} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      options={purchaseTypes}
                      isLoading={isLoading}
                      isDisabled={isDisabled || isLoading}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      styles={reactSelectStyles<PurchaseType>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="supplier"
              control={control}
              rules={{ required: type.value == "purchase" }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.suppliers.singular} error={fieldState.error} hidden={type.value === "leasing"}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      options={suppliers.data?.items}
                      isLoading={isLoading}
                      isDisabled={isDisabled || isLoading}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<SupplierSearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="leasingCompany"
              control={control}
              rules={{ required: type.value == "leasing" }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.t.leasingCompanies.singular}
                  error={fieldState.error}
                  hidden={type.value === "purchase"}
                >
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading}
                      isLoading={isLoading}
                      options={leasingCompanies.data?.items}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<LeasingCompanySearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="manufacturer"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.manufacturers.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      options={manufacturers.data?.items}
                      isLoading={isLoading}
                      isDisabled={isDisabled || isLoading}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<ManufacturerSearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.t.common.model} error={errors.model}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("model", {
                    required: true,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.purchasePriceInputForm} isOptional>
              {({ labelId }) => {
                const costGross = watch("costGross");
                const costNet = watch("costNet");
                const currency = watch("currency");

                const isCostNetRequired = !!currency && !costGross;
                const isCostGrossRequired = !!currency && !costNet;
                const isCurrencyRequired = !!costNet || !!costGross;

                register("costNet", { required: isCostNetRequired });
                register("costGross", { required: isCostGrossRequired });
                register("currency", { required: isCurrencyRequired });

                useEffect(() => {
                  const c = carPurchase?.costNet
                    ? carPurchase.costNet.ForeignValue
                      ? carPurchase.costNet.ForeignValue.Currency
                      : euroCurrency?.value
                    : carPurchase?.costGross
                      ? carPurchase.costGross.ForeignValue
                        ? carPurchase.costGross.ForeignValue.Currency
                        : euroCurrency?.value
                      : undefined;

                  const currency = currencies.find((x) => c === x.value) ?? null;
                  setValue("currency", currency ?? euroCurrency ?? null);
                }, [carPurchase?.costNet?.ForeignValue?.Currency, carPurchase?.costGross?.ForeignValue?.Currency]);

                return (
                  <CostInput
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    id={labelId}
                    onSetCostGross={(v) => setValue("costGross", v)}
                    onSetCostNet={(v) => setValue("costNet", v)}
                    onSetCurrency={(v) => setValue("currency", v)}
                    costGross={costGross ?? null}
                    costNet={costNet}
                    currency={currency}
                    isGrossInvalid={!!errors.costGross}
                    isNetInvalid={!!errors.costNet}
                    isCurInvalid={!!errors.currency}
                  />
                );
              }}
            </FormField>

            <Controller
              name="purchaseDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (!value) return i18n.t.common.invalidDate;
                  const d = new Date(value);
                  if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.orderDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => setValue("purchaseDate", v!)}
                      isInvalid={isInvalid}
                      value={watch("purchaseDate")}
                      disabled={isDisabled || isLoading}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="state"
              control={control}
              rules={{ required: true }}
              // disabled={!showAssign}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.state} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      inputId={labelId}
                      options={states}
                      isLoading={isLoading}
                      isDisabled={isDisabled || isLoading || carPurchase?.state == "assigned"}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      styles={reactSelectStyles<State>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="plannedArrivalDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (!value) return i18n.t.common.invalidDate;
                  const d = new Date(value);
                  if (d.getFullYear() < 1000) return i18n.t.common.invalidDate;
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.plannedArrivalDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => setValue("plannedArrivalDate", v!)}
                      isInvalid={isInvalid}
                      value={watch("plannedArrivalDate")}
                      disabled={isDisabled || isLoading}
                    />
                  )}
                </FormField>
              )}
            />

            <FormField label={i18n.t.common.optionals} error={errors.optionals} isOptional fullWidth>
              {({ labelId, isInvalid }) => (
                <Textarea
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("optionals", {
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
          </FormGrid>
        );
      }}
    </Form>
  );
};

export default CarPurchaseForm;
